import { AppTableControls } from 'common/TableControls/TableControls';
import { AppMultipleSelect, AppSelect } from 'shared/UI/UIKit/Select';
import { formatSubjectWithLevel } from 'shared/helpers/utils/subject-display';
import { educationParallelTextRepresentation } from 'entities/education-class';
import type { EducationClass } from 'services/api';
import type { Subject } from 'services/api/subjects';
import type { AppTableColumn } from 'shared/UI/UIKit/Table';
import type { SubjectRow } from 'entities/teacher-workload/form/types';
import styles from './styles.module.scss';

type SubjectOption = {
  value: string;
  label: string;
  subjectIds: number[];
};

type GetColumnsProps = {
  handleDeleteRow: (key: string) => void;
  handleSubjectChange: (key: string, subjectName: string) => void;
  handleHoursChange: (key: string, workloadHours: number | null) => void;
  handleCopyRow: (educationalClassesIds: number[] | null) => void;
  handleEducationalClassesChange: (key: string, educationalClasses: number[]) => void;
  selectedSubjectIds: number[];
  subjectOptions: SubjectOption[];
  subjectsById: Record<number, Subject>;
  subjectsLoading: boolean;
  availableEducationClasses: EducationClass[];
  subjectsCount: number;
};

export const getColumns = ({
  handleDeleteRow,
  handleSubjectChange,
  handleCopyRow,
  handleEducationalClassesChange,
  selectedSubjectIds,
  subjectOptions,
  subjectsById,
  subjectsLoading,
  availableEducationClasses,
  subjectsCount,
}: GetColumnsProps): AppTableColumn<SubjectRow>[] => [
  {
    title: (
      <>
        Предмет <span style={{ color: 'red' }}>*</span>
      </>
    ),
    dataIndex: ['subjectId'],
    key: 'subjectId',
    minWidth: 220,
    className: styles.subjectColumn,
    render: (value: number | null, record: SubjectRow) => {
      const subject = value !== null ? subjectsById[value] : undefined;

      return (
        <AppSelect
          value={value ?? undefined}
          loading={subjectsLoading}
          showSearch
          optionFilterProp="label"
          dropdownMatchSelectWidth={false}
          dropdownClassName={styles.subjectSelectDropdown}
          options={[
            ...(subject
              ? [
                  {
                    value,
                    label: formatSubjectWithLevel(subject.name, subject.educationLevel),
                    disabled: true,
                  },
                ]
              : []),
            ...subjectOptions.map((subjectOption) => ({
              value: subjectOption.value,
              label: subjectOption.label,
              disabled: subjectOption.subjectIds.every((subjectId) =>
                selectedSubjectIds.includes(subjectId),
              ),
            })),
          ]}
          onChange={(subjectName) => {
            if (typeof subjectName === 'string') {
              handleSubjectChange(record.key, subjectName);
            }
          }}
        />
      );
    },
  },
  {
    title: 'Классы',
    dataIndex: ['educationalClassIds'],
    key: 'educationalClassIds',
    minWidth: 220,
    maxWidth: 250,
    render: (value: number[] | null, record: SubjectRow) => {
      return (
        <AppMultipleSelect
          value={value || []}
          options={
            availableEducationClasses?.map((educationalClass: EducationClass) => ({
              label: `${educationParallelTextRepresentation[educationalClass.parallel]}${
                educationalClass.litera
              } класс`,
              value: educationalClass.id,
            })) || []
          }
          style={{ height: 'auto', minHeight: '4rem' }}
          onChange={(educationalClasses) =>
            handleEducationalClassesChange(record.key, educationalClasses)
          }
        />
      );
    },
  },
  {
    key: 'controls',
    title: `Предметов: ${subjectsCount}`,
    dataIndex: [],
    minWidth: 128,
    render: (_: any, record: SubjectRow) => {
      return (
        <AppTableControls
          copy={() => handleCopyRow(record.educationalClassIds)}
          remove={() => handleDeleteRow(record.key)}
        />
      );
    },
  },
];
